// Scorecard Builder — sections + questions + weights. No JSON.
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, ClipboardCheck, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function ScorecardBuilderPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/resources/scorecards"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew() {
    setForm({ name: "", description: "", is_active: true, pass_threshold: 70, sections: [{ title: "Opening", questions: [{ q: "Greeted with full name + company?", weight: 5 }] }] });
    setEditing("new");
  }
  function startEdit(r) {
    setForm({ name: r.name, description: r.description||"", is_active: !!r.is_active, pass_threshold: r.data?.pass_threshold||70, sections: r.data?.sections || [] });
    setEditing(r);
  }
  function addSection() { setForm({ ...form, sections: [...form.sections, { title: "New Section", questions: [] }] }); }
  function updateSection(i, patch) { setForm({ ...form, sections: form.sections.map((s,j)=>j===i?{...s,...patch}:s) }); }
  function removeSection(i) { setForm({ ...form, sections: form.sections.filter((_,j)=>j!==i) }); }
  function moveSection(i, dir) { const a=[...form.sections]; const t=i+dir; if (t<0||t>=a.length) return; [a[i],a[t]]=[a[t],a[i]]; setForm({...form, sections:a}); }
  function addQ(si) { const s=form.sections[si]; updateSection(si, { questions: [...(s.questions||[]), { q: "", weight: 5 }] }); }
  function updateQ(si, qi, patch) { const s=form.sections[si]; updateSection(si, { questions: s.questions.map((q,j)=>j===qi?{...q,...patch}:q) }); }
  function removeQ(si, qi) { const s=form.sections[si]; updateSection(si, { questions: s.questions.filter((_,j)=>j!==qi) }); }

  const totalWeight = (form.sections||[]).reduce((t,s)=> t + (s.questions||[]).reduce((tt,q)=>tt + (Number(q.weight)||0), 0), 0);

  async function save() {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description||"", is_active: !!form.is_active, data: { weight_total: totalWeight, pass_threshold: Number(form.pass_threshold)||70, sections: form.sections } };
      if (editing === "new") await api.post("/admin/resources/scorecards", payload);
      else await api.patch(`/admin/resources/scorecards/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/admin/resources/scorecards/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="sc-title">QM Scorecards</h1>
          <p className="text-sm text-muted-foreground mt-1">Build sections + questions + weights. Used by Supervisors / QA reviewers to evaluate calls and chats.</p>
        </div>
        {canWrite && <Button data-testid="add-sc-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4"/> New scorecard</Button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Sections</th><th className="px-4 py-2">Total weight</th><th className="px-4 py-2">Pass %</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-24">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-sc-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-primary"/><span className="font-medium">{r.name}</span></div></td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{(r.data?.sections||[]).length}</Badge></td>
                  <td className="px-4 py-2.5">{r.data?.weight_total ?? "—"}</td>
                  <td className="px-4 py-2.5">{r.data?.pass_threshold ?? "—"}</td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">On</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">
                    {canWrite && <Button data-testid={`edit-sc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                    {canDelete && <Button data-testid={`del-sc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No scorecards yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing==="new"?"New scorecard":`Edit ${editing?.name}`}</DialogTitle><DialogDescription>Add sections and questions. Weights sum to the total.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="sc-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
              <div className="flex items-center gap-2 mt-7"><Switch data-testid="sc-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
              <div><Label>Pass threshold (%)</Label><Input data-testid="sc-pass" type="number" value={form.pass_threshold||70} onChange={(e)=>setForm({...form, pass_threshold:Number(e.target.value)})} /></div>
              <div className="sm:col-span-2"><Label>Total weight (auto)</Label><Input value={totalWeight} disabled /></div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold">Sections</h3>
                <Button data-testid="sc-add-section" onClick={addSection} variant="outline" className="gap-1.5"><Plus className="w-4 h-4"/> Add section</Button>
              </div>
              <div className="space-y-3">
                {(form.sections||[]).map((s, i) => (
                  <Card key={i} className="p-3" data-testid={`sc-section-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={s.title||""} onChange={(e)=>updateSection(i,{title:e.target.value})} className="font-semibold" placeholder="Section title" data-testid={`sc-section-title-${i}`} />
                      <div className="ml-auto flex gap-1">
                        <Button onClick={()=>moveSection(i,-1)} variant="ghost" size="icon" className="h-7 w-7"><ArrowUp className="w-3.5 h-3.5"/></Button>
                        <Button onClick={()=>moveSection(i,1)}  variant="ghost" size="icon" className="h-7 w-7"><ArrowDown className="w-3.5 h-3.5"/></Button>
                        <Button onClick={()=>removeSection(i)}  variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`sc-section-rm-${i}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {(s.questions||[]).map((q, qi) => (
                        <div key={qi} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`sc-q-${i}-${qi}`}>
                          <Input placeholder="Question" value={q.q||""} onChange={(e)=>updateQ(i, qi, { q: e.target.value })} />
                          <Input type="number" placeholder="Weight" value={q.weight||0} onChange={(e)=>updateQ(i, qi, { weight: Number(e.target.value) })} />
                          <Button onClick={()=>removeQ(i, qi)} variant="ghost" size="icon" className="h-8 w-8 text-destructive"><Trash2 className="w-3.5 h-3.5"/></Button>
                        </div>
                      ))}
                      <Button onClick={()=>addQ(i)} variant="outline" size="sm" className="gap-1" data-testid={`sc-add-q-${i}`}><Plus className="w-3.5 h-3.5"/> Add question</Button>
                    </div>
                  </Card>
                ))}
                {(form.sections||[]).length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No sections yet.</div>}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="sc-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
